import { ComputedRef, DeepReadonly, readonly, Ref, ref, watch, onUnmounted } from "vue";
import { RealtimeClient } from "@openai/realtime-api-beta";
import { MicVAD } from "@ricky0123/vad-web";
import semver from "semver";
import { getIosVersion } from "@/shared";
import { WavRecorder, WavStreamPlayer } from '@/lib/wavtools';
import { RealtimeUtils } from "@openai/realtime-api-beta";
import useSpeechFrequencies from "@/useSpeechFrequencies";
import throttle from "lodash.throttle";
import { usePage } from "@inertiajs/vue3";

interface UseVoiceChat {
    state: DeepReadonly<Ref<'idle' | 'initializing' | 'recording' | 'paused' | 'stopping' | 'error'>>;
    start(): Promise<void>;
    pause(): Promise<void>;
    resume(): Promise<void>;
    stop(): Promise<void>;
    userSpeechFrequencies: DeepReadonly<Ref<Float32Array>>;
    isUserSpeaking: ComputedRef<boolean>;
    impersonatorSpeechFrequencies: DeepReadonly<Ref<Float32Array>>;
    isImpersonatorSpeaking: ComputedRef<boolean>;
    latestImpersonatorTranscript: DeepReadonly<Ref<string>>;
}

interface UseVoiceChatOptions {
    turnDetectionType?: 'browser' | 'server_vad';
    // TODO: accept whole VAD configuration, silence_duration ... and accept it in ProcessRealtimeMessages
}

interface RoleplayProps {
    UUID: string;
    runUUID: string;
    userID: number;
    rfc5646Lang: string;
}

interface VADOptions {
    stream: MediaStream;

    /**
     * Duration of silence to detect speech stop (in milliseconds). Defaults to 500ms. With shorter values the model will respond more quickly, but may jump in on short pauses from the user.
     */
    silenceDurationMs?: number;

    onSpeechStart?: () => void;
    onSpeechEnd?: () => void;
    onSpeechRealStart?: () => void;
    onVADMisfire?: () => void;
}

async function initializeVAD(options: VADOptions) {
    const { silenceDurationMs = 1500 } = options;
    
    const numOfSamplesInFrame = 512;

    // Sample rate conversion is performed on input audio so that the processed audio has a sample rate of 16000.
    // see: https://docs.vad.ricky0123.com/user-guide/algorithm/
    const samplesPerSecond = 16000;
    
    const frameDurationMs = numOfSamplesInFrame / samplesPerSecond * 1000;
    const redemptionFrames = Math.round(silenceDurationMs / frameDurationMs);

    // minimum amount of speech in audio segment
    // for example since speech starts until the speech ends it's 1000ms audio
    // 200 means in that segment needs to be at least of 200ms of speech (n o frames above threshold)
    const minSpeechDurationMsInSegment = 192;
    const minSpeechFrames = Math.max(1, Math.round(minSpeechDurationMsInSegment / frameDurationMs));

    const vad = await MicVAD.new({
        stream: options.stream,
        model: "v5",
        positiveSpeechThreshold: 0.70,
        negativeSpeechThreshold: 0.45,
        preSpeechPadFrames: 6,
        redemptionFrames,
        minSpeechFrames,

        onSpeechStart: options.onSpeechStart,
        onSpeechRealStart: options.onSpeechRealStart,
        onVADMisfire: options.onVADMisfire,
        onSpeechEnd: options.onSpeechEnd,
    });

    return vad;
}

const SAMPLE_RATE = 24000;

export function useVoiceChat(provider: 'realtime' | 'custom', roleplay: RoleplayProps, options: UseVoiceChatOptions = {}): UseVoiceChat {
    if (provider === 'realtime') {
        return useRealtimeVoiceChat(roleplay, options);
    } else if (provider === 'custom') {
        return useCustomVoiceChat(roleplay, options);
    }

    throw new Error('Invalid voice chat provider');
}

export function useRealtimeVoiceChat(roleplay: RoleplayProps, options: UseVoiceChatOptions = {}): UseVoiceChat {
    const wavRecorder = new WavRecorder({ sampleRate: SAMPLE_RATE });
    const wavStreamPlayer = new WavStreamPlayer({ sampleRate: SAMPLE_RATE });
    let realtimeClient: RealtimeClient | null = null;
    let browserVAD: MicVAD | null = null;

    const state = ref<UseVoiceChat['state']['value']>('idle');

    const { 
        speechFrequencies: userSpeechFrequencies, isSpeaking: isUserSpeaking 
    } = useSpeechFrequencies(() => wavRecorder.recording ? wavRecorder.getFrequencies('voice').values : new Float32Array(72));

    const { 
        speechFrequencies: impersonatorSpeechFrequencies, 
        isSpeaking: isImpersonatorSpeaking 
    } = useSpeechFrequencies(() => wavStreamPlayer.analyser ? wavStreamPlayer.getFrequencies('voice').values : new Float32Array(72));


    function getTurnDetectionType(): UseVoiceChatOptions['turnDetectionType'] {
        const iosVersion = getIosVersion();
    
        if (iosVersion && semver.major(iosVersion) <= 18) {
            return 'server_vad';
        }
    
        return 'browser';
    }

    const { turnDetectionType = getTurnDetectionType() } = options;

    const throttledRealtimeEvents = new Map<string, () => void>();

    function throttleById(id: string, callback: () => void) {
        if (!throttledRealtimeEvents.has(id)) {
            throttledRealtimeEvents.set(id, throttle(callback, 1000));
        }

        throttledRealtimeEvents.get(id)();
    }

    const latestImpersonatorTranscript = ref('');
    const lastDeltaEvent = ref(null);

    watch(lastDeltaEvent, (deltaEvent, oldDeltaEvent) => {
        if (deltaEvent.item_id !== oldDeltaEvent?.item_id) {
            latestImpersonatorTranscript.value = '';
        }

        latestImpersonatorTranscript.value += deltaEvent.delta;
    });

    function initializeRealtimeClient() {
        // on production we use nginx as a reverse proxy for the websocket server on /ws path
        const isLocalhost = location.hostname === 'localhost';
        const protocol = isLocalhost ? 'ws' : 'wss';
        const wsPort = isLocalhost ? ':3000' : '';
        const wsPath = isLocalhost ? '/' : '/ws/';
    
        const url = new URL(`${protocol}://${location.hostname}${wsPort}${wsPath}`);
        url.searchParams.set('sessionID', roleplay.runUUID);
        url.searchParams.set('roleplayID', roleplay.UUID);
        url.searchParams.set('rfc5646Lang', roleplay.rfc5646Lang);
        url.searchParams.set('userID', roleplay.userID.toString());
    
        const realtimeClient = new RealtimeClient({ url: url.toString(), debug: false });
    
        realtimeClient.on('error', (event) => console.error(event));
    
        realtimeClient.on('conversation.interrupted', async () => {
            const trackSampleOffset = await wavStreamPlayer.interrupt();
    
            // if there was already played part of the assistant's response
            // cancel it and truncate it
            if (trackSampleOffset?.trackId) {
                const { trackId, offset } = trackSampleOffset;
                realtimeClient.cancelResponse(trackId, offset);
            } else {
                // otherwise just cancel the active response (no truncation needed)
                realtimeClient.cancelResponse(null);
            }
        });
    
        realtimeClient.realtime.on('server.conversation.item.truncated', (event) => {
            const item = realtimeClient.conversation.getItem(event.item_id);
    
            throttleById(`truncate-${item.id}`, function truncateMessage() {
                if (item && item.formatted.audio?.length) {
                    const audioData = item.formatted.audio;
                    const encodedAudio = RealtimeUtils.arrayBufferToBase64(audioData);
        
                    realtimeClient.realtime.send('message.create', {
                        id: item.id,
                        content: {
                            type: 'audio',
                            audio: encodedAudio
                        },
                        role: 'assistant',
                    });
                }
            });
        });
    
        realtimeClient.realtime.on('server.message.created', (event) => {
            //
        });
    
        realtimeClient.realtime.on('server.response.audio_transcript.delta', (event) => {
            lastDeltaEvent.value = event;
        });
    
        realtimeClient.on('conversation.updated', async ({ item, delta }) => {
            const items = realtimeClient.conversation.getItems();
    
            if (delta?.audio) {
                wavStreamPlayer.add16BitPCM(delta.audio, item.id);
            }
    
            if (item.status === 'completed' && item.formatted.audio?.length) {
                const wavFile = await WavRecorder.decode(
                    item.formatted.audio,
                    24000,
                    24000
                );
    
                item.formatted.file = wavFile;
            }
    
            throttleById(`update-content-${item.id}`, function sendUpdateToBE() {
                if (!realtimeClient.isConnected()) return;
    
                if (item.type !== 'message') return;
        
                let text = null;
        
                const contentType = item.content[0]?.type;
        
                if (['text', 'input_text'].includes(contentType)) return;
        
                if (item.role === 'user') {
                    if (contentType === 'input_audio') {
                        text = item.formatted.transcript;
                        //
                    } else if (contentType === 'input_text') {
                        text = item.formatted.text;
                    } else {
                        return;
                    }
                } else if (item.role === 'assistant') {
                    if (contentType === 'audio') {
                        text = item.formatted.transcript;
                    } else if (contentType === 'text') {
                        text = item.formatted.text;
                    } else {
                        return;
                    }
                } else {
                    return;
                }
        
                realtimeClient.realtime.send('message.create', {
                    id: item.id,
                    content: {
                        type: 'text',
                        text
                    },
                    role: item.role,
                });
            });
        });

        return realtimeClient;
    }

    let prefixBuffer = new Int16Array();
    const PREFIX_PADDING_MS = 200;
    const numOfPrefixSamples = (PREFIX_PADDING_MS / 1000) * SAMPLE_RATE;

    let isSpeaking = false;

    // store the offset where the impersonator was interrupted
    let wavStreamPlayerOffset: { trackId: string, offset: number } | null = null;

    // when it's probably a speech, but still a chance it's a false positive
    // so we don't cancel the response just yet
    async function onSpeechStart() {
        isSpeaking = true;

        // pause the impersonator ASAP
        // so it does not mix into user's microphone
        wavStreamPlayer.pause();

        // and remember the point when we interrupted it
        // so when it confirms it's a real speech, we can tell openAI
        // precise moment when to trim impersonator's response
        wavStreamPlayerOffset = await wavStreamPlayer.getTrackSampleOffset();
    }

    // when we're sure it's a real speech
    // we cancel the response and tell openAI to trim the impersonator's response
    async function onSpeechRealStart() {
        await wavStreamPlayer.interrupt(); // throw away the queued audio

        // if there was already played part of the assistant's response
        // cancel it and truncate it
        if (wavStreamPlayerOffset?.trackId) {
            const { trackId, offset } = wavStreamPlayerOffset;
            realtimeClient.cancelResponse(trackId, offset);
        } else {
            // otherwise just cancel the active response (no truncation needed)
            realtimeClient.cancelResponse(null);
        }
    }

    // when we're sure it's not realse speech but it was false positive
    // we resume the currently paleyd impersonator
    function onVADMisfire() {
        isSpeaking = false;
        realtimeClient.realtime.send('input_audio_buffer.clear', undefined);

        // it was just misfire, resume whatever was impersonator talking
        wavStreamPlayer.resume();

        wavStreamPlayerOffset = null;
    }

    function onSpeechEnd() {
        realtimeClient.createResponse();
        isSpeaking = false;
    }

    const onAudioChunk: Parameters<typeof wavRecorder.record>[0] = (data) => {
        if (turnDetectionType === 'server_vad') {
            realtimeClient.appendInputAudio(data.mono);
        } else if (turnDetectionType === 'browser') {
            // merge prefix buffer with audio chunk
            let audioChunkWithPrefix = RealtimeUtils.mergeInt16Arrays(prefixBuffer, data.mono);
    
            if (!isSpeaking) {
                // keep only the most recent samples based on PREFIX_PADDING_MS
                prefixBuffer = audioChunkWithPrefix.slice(-numOfPrefixSamples);
            } else {
                realtimeClient.appendInputAudio(audioChunkWithPrefix)
                // reset prefix buffer
                prefixBuffer = new Int16Array();
            }
        }
    }

    async function start() {
        state.value = 'initializing';
        try {
            realtimeClient = initializeRealtimeClient();
            
            [browserVAD] = await Promise.all([
                wavRecorder.begin()
                .then(() => {
                    wavRecorder.record(onAudioChunk);
                    return turnDetectionType === 'browser' 
                        ? initializeVAD({ 
                            stream: wavRecorder.stream, 
                            onSpeechStart, 
                            onSpeechEnd,
                            onSpeechRealStart,
                            onVADMisfire,
                        }) 
                        : null;
                }),
                wavStreamPlayer.connect(),
                realtimeClient.connect({ model: 'gpt-4o-realtime-preview-2024-12-17' }).then(() => realtimeClient.waitForSessionCreated()),
            ]);

            if (browserVAD) {
                browserVAD.start();
            }

            state.value = 'recording';

            realtimeClient.realtime.send('roleplayRun.continue', {
                roleplayRunUUID: roleplay.runUUID,
                turnDetectionType
            });
        
            // Let the impersonator start speaking first
            realtimeClient.realtime.send('response.create', undefined);
        } catch (error) {
            state.value = 'error';
        }
    }

    async function stop() {
        state.value = 'stopping';

        if (realtimeClient && realtimeClient.isConnected()) {
            realtimeClient.dispatch('conversation.interrupted', undefined);
            realtimeClient.realtime.send('input_audio_buffer.clear', undefined);

            // wait for the server to truncate the conversation item, max 1000ms
            new Promise<void>(resolve => {
                realtimeClient.realtime.on('server.conversation.item.truncated', (event) => {
                    resolve();
                });
    
                setTimeout(() => resolve(), 1000);
            }).then(() => {
                realtimeClient?.reset();
                realtimeClient = null;
            });
        }


        if (browserVAD) {
            browserVAD.pause();
            browserVAD.destroy();
            browserVAD = null;
        }

        if (wavRecorder.getStatus() !== 'ended') {
            await wavRecorder.quit();
        }

        state.value = 'idle';
    }

    async function pause() {
        realtimeClient.dispatch('conversation.interrupted', undefined);
        realtimeClient.realtime.send('input_audio_buffer.clear', undefined);

        if (browserVAD) {
            browserVAD.pause();
        }

        if (wavRecorder.recording) {
            await wavRecorder.pause();
        }

        state.value = 'paused';
    }

    async function resume() {
        if (state.value !== 'paused') {
            throw new Error('Voice chat is not paused');
        }

        if (!wavRecorder.recording) {
            await wavRecorder.record(onAudioChunk);
        }

        if (browserVAD) {
            browserVAD.start();
        }

        state.value = 'recording';
    }

    onUnmounted(stop);

    return {
        start,
        stop,
        pause,
        resume,
        state: readonly(state),
        userSpeechFrequencies,
        isUserSpeaking,
        impersonatorSpeechFrequencies,
        isImpersonatorSpeaking,
        latestImpersonatorTranscript,
    }
}

export function useCustomVoiceChat(roleplay: RoleplayProps, options: UseVoiceChatOptions = {}): UseVoiceChat {
    const wavRecorder = new WavRecorder({ sampleRate: SAMPLE_RATE });
    const wavStreamPlayer = new WavStreamPlayer({ sampleRate: SAMPLE_RATE });
    let browserVAD: MicVAD | null = null;
    const customClient = new CustomClient({ url: new URL(`/voice-chat/${roleplay.runUUID}`, location.origin), debug: false });

    const state = ref<UseVoiceChat['state']['value']>('idle');

    const { 
        speechFrequencies: userSpeechFrequencies, isSpeaking: isUserSpeaking 
    } = useSpeechFrequencies(() => wavRecorder.recording ? wavRecorder.getFrequencies('voice').values : new Float32Array(72));

    const { 
        speechFrequencies: impersonatorSpeechFrequencies, 
        isSpeaking: isImpersonatorSpeaking 
    } = useSpeechFrequencies(() => wavStreamPlayer.analyser ? wavStreamPlayer.getFrequencies('voice').values : new Float32Array(72));

    const { turnDetectionType = 'browser' } = options;

    if (turnDetectionType === 'server_vad') {
        throw new Error("Server VAD is not implemented for custom voice chat");
    }

    const latestImpersonatorTranscript = ref('');
    const lastDeltaEvent = ref(null);

    watch(lastDeltaEvent, (deltaEvent, oldDeltaEvent) => {
        if (deltaEvent.item_id !== oldDeltaEvent?.item_id) {
            latestImpersonatorTranscript.value = '';
        }

        latestImpersonatorTranscript.value += deltaEvent.delta;
    });

    async function interrupt() {
        customClient.send('response.cancel', undefined);

        const trackSampleOffset = await wavStreamPlayer.interrupt();
        if (trackSampleOffset?.trackId) {
            const { trackId, offset: sampleCount } = trackSampleOffset;
            customClient.send('conversation.item.truncate', {
                // messageId, // if we omit id, the last assistant message will be truncated
                audio_end_ms: Math.floor(
                  (sampleCount / SAMPLE_RATE) * 1000,
                ),
            });
        }
    }

    function onSpeechStart() {
        interrupt();
        isSpeaking = true;
    }

    async function onSpeechEnd() {
        isSpeaking = false;
        await customClient.send('input_audio_buffer.commit');
        await customClient.send('response.create');
    }

    let prefixBuffer = new Int16Array();
    const PREFIX_PADDING_MS = 200;
    const numOfPrefixSamples = (PREFIX_PADDING_MS / 1000) * SAMPLE_RATE;

    let isSpeaking = false;

    const onAudioChunk: Parameters<typeof wavRecorder.record>[0] = (data) => {
        // merge prefix buffer with audio chunk
        let audioChunkWithPrefix = RealtimeUtils.mergeInt16Arrays(prefixBuffer, data.mono);

        if (!isSpeaking) {
            // keep only the most recent samples based on PREFIX_PADDING_MS
            prefixBuffer = audioChunkWithPrefix.slice(-numOfPrefixSamples);
        } else {
            customClient.send('input_audio_buffer.append', audioChunkWithPrefix);
            // reset prefix buffer
            prefixBuffer = new Int16Array();
        }
    }

    customClient.on('response.text.delta', (event: { type: 'response.text.delta', delta: string, item_id: string }) => {
        lastDeltaEvent.value = event;
    });

    customClient.on('response.audio.delta', (event) => {
        const { delta: base64EncodedPCMChunk, item_id } : { delta: string, item_id: string } = event;
        const arrayBuffer = RealtimeUtils.base64ToArrayBuffer(base64EncodedPCMChunk);
        wavStreamPlayer.add16BitPCM(arrayBuffer, item_id);
    });

    async function start() {
        state.value = 'initializing';
        try {
            [browserVAD] = await Promise.all([
                wavRecorder.begin()
                .then(() => {
                    wavRecorder.record(onAudioChunk);
                    return initializeVAD({ stream: wavRecorder.stream, onSpeechStart, onSpeechEnd, silenceDurationMs: 2000 });
                }),
                wavStreamPlayer.connect(),
                customClient.connect(),
            ]);

            if (browserVAD) {
                browserVAD.start();
            }

            state.value = 'recording';

            // Let the impersonator start speaking first
            customClient.send('response.create');
        } catch (error) {
            state.value = 'error';
        }
    }

    async function stop() {
        state.value = 'stopping';

        if (browserVAD) {
            browserVAD.pause();
            browserVAD.destroy();
            browserVAD = null;
        }

        customClient.send('input_audio_buffer.clear', undefined);
        interrupt();

        if (wavRecorder.getStatus() !== 'ended') {
            await wavRecorder.quit();
        }

        customClient.disconnect();

        state.value = 'idle';
    }

    async function pause() {
        customClient.send('input_audio_buffer.clear', undefined);
        interrupt();

        if (browserVAD) {
            browserVAD.pause();
        }

        if (wavRecorder.recording) {
            await wavRecorder.pause();
        }

        state.value = 'paused';
    }

    async function resume() {
        if (state.value !== 'paused') {
            throw new Error('Voice chat is not paused');
        }

        if (!wavRecorder.recording) {
            await wavRecorder.record(onAudioChunk);
        }

        if (browserVAD) {
            browserVAD.start();
        }

        state.value = 'recording';
    }

    onUnmounted(stop);

    return {
        start,
        stop,
        pause,
        resume,
        state: readonly(state),
        userSpeechFrequencies,
        isUserSpeaking,
        impersonatorSpeechFrequencies,
        isImpersonatorSpeaking,
        latestImpersonatorTranscript,
    }
}

// maybe custom Event classes
type ClientEvent = 'input_audio_buffer.append' | 'input_audio_buffer.clear' | 'input_audio_buffer.commit' | 'response.create' | 'response.cancel' | 'conversation.item.truncate';
const serverEvents = ['response.created', 'response.cancelled', 'response.audio.delta', 'response.text.delta', 'conversation.item.truncated'];
type ServerEvent = typeof serverEvents[number];
type ServerEventListener = (data: any) => void;

type CustomClientOptions = {
    url: URL;
    debug?: boolean;
}

// hide how we communicate with the server, e.g. websocket, server-events, webRTC, etc.
class CustomClient {
    private evtSource: EventSource;
    private baseURL: URL | string;
    private listeners: Map<ServerEvent, ServerEventListener[]>;
    private options: Omit<CustomClientOptions, 'url'>;

    constructor({ url, ...options }: CustomClientOptions) {
        this.baseURL = new URL(url);
        this.listeners = new Map();

        this.options = {
            debug: false,
            ...options
        }
    }
    
    connect() {
        return new Promise((resolve, reject) => {
            this.evtSource = new EventSource(`${this.baseURL.toString()}/connect`, {
                withCredentials: true,
            });
    
            this.evtSource.onopen = () => {
                resolve(true);
                if (this.options.debug) {
                    console.log('connected to server');
                }
            };

            this.evtSource.addEventListener('json', this.handleJsonMessage.bind(this));
    
            this.evtSource.onerror = (event) => {
                console.error(event);
            };
        });
    }

    disconnect() {
        this.evtSource.close();
        this.evtSource.removeEventListener('json', this.handleJsonMessage.bind(this));
    }

    // maybe return cancel function to cancel the event, for example cancel the response.create
    send(event: ClientEvent, data?: any) {
        if (this.options.debug) {
            console.log('client event:', event, data)
        }

        switch (event) {
            case 'input_audio_buffer.append':
                return fetch(`${this.baseURL.toString()}/input-audio-buffer/append`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': usePage().props.csrf_token as string,
                    },
                    body: JSON.stringify({
                        audio: RealtimeUtils.arrayBufferToBase64(data)
                    }),
                    credentials: 'include'
                }).then(async (response) => {
                    if (this.options.debug) {
                        console.log('server response:', await response.json());
                    }
                    return response;
                });

            case 'input_audio_buffer.clear':
                return fetch(`${this.baseURL.toString()}/input-audio-buffer/clear`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': usePage().props.csrf_token as string,
                    },
                    credentials: 'include'
                }).then(async (response) => {
                    if (this.options.debug) {
                        console.log('server response:', await response.json());
                    }
                    return response;
                });

            case 'input_audio_buffer.commit':
                return fetch(`${this.baseURL.toString()}/input-audio-buffer/commit`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': usePage().props.csrf_token as string,
                    },
                    credentials: 'include'
                }).then(async (response) => {
                    if (this.options.debug) {
                        console.log('server response:', await response.json());
                    }
                    return response;
                });

            case 'response.create':
                return fetch(`${this.baseURL.toString()}/response/create`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': usePage().props.csrf_token as string,
                    },
                    credentials: 'include'
                }).then(async (response) => {
                    if (this.options.debug) {
                        console.log('server response:', await response.json());
                    }
                    return response;
                });

            case 'response.cancel':
                return fetch(`${this.baseURL.toString()}/response/cancel`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': usePage().props.csrf_token as string,
                    },
                    credentials: 'include'
                }).then(async (response) => {
                    if (this.options.debug) {
                        console.log('server response:', await response.json());
                    }
                    return response;
                });

            case 'conversation.item.truncate':
                return fetch(`${this.baseURL.toString()}/conversation/item/truncate`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': usePage().props.csrf_token as string,
                    },
                    body: JSON.stringify({
                        audio_end_ms: data?.audio_end_ms,
                        message_id: data?.message_id
                    }),
                    credentials: 'include'
                }).then(async (response) => {
                    if (this.options.debug) {
                        console.log('server response:', await response.json());
                    }
                    return response;
                });
        }
    }

    on(event: ServerEvent, callback: ServerEventListener) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }

        this.listeners.get(event).push(callback);
    }

    private handleJsonMessage(evt: MessageEvent) {
        // TODO: handle comments : -- keep alive

        if (this.options.debug) {
            console.log('server event:', evt.data);
        }

        const event = JSON.parse(evt.data);
        if (!serverEvents.includes(event.type)) return;

        const listeners = this.listeners.get(event.type);
        if (listeners) {
            listeners.forEach(callback => callback(event));
        }
    }
}

export async function requestMicrophonePermission() {
    if (
        !navigator.mediaDevices ||
        !('getUserMedia' in navigator.mediaDevices)
    ) {
        throw new Error('Could not request user media');
    }
    try {
        const config = { audio: true };

        await navigator.mediaDevices.getUserMedia(config);
        return 'granted';
    } catch (err) {
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
            return 'denied';
        }

        throw new Error('Could not start media stream');
    }
}
